import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from "react-router-dom";

import Login           from "./pages/Login";
import PrivacyPolicy   from "./pages/PrivacyPolicy";
import ProtectedRoute  from "./routes/ProtectedRoute";
import AdminLayout     from "./layouts/AdminLayout";
import StandaloneLayout from "./layouts/StandaloneLayout";

/* Pages */
import AdminDashboard  from "./pages/AdminDashboard";
import Products        from "./pages/Products";
import ProductDetails  from "./pages/ProductDetails";
import Companies       from "./pages/Companies";
import AddCompany      from "./pages/AddCompany";
import EditCompany     from "./pages/EditCompany";
import CompanyDetails  from "./pages/CompanyDetails";
import AllDevices      from "./pages/AllDevices";
import AddDevice       from "./pages/AddDevice";
import DevicePage      from "./pages/DevicePage";
import DeviceHealth    from "./pages/DeviceHealth";
import Alerts          from "./pages/Alerts";
import Maintenance     from "./pages/Maintenance";
import Reports         from "./pages/Reports";
import ESGReports      from "./pages/ESGReports";
import Roles           from "./pages/Roles";
import Users           from "./pages/Users";
import Admins          from "./pages/Admins";
import ApiKeys         from "./pages/ApiKeys";
import ApiManagerTool  from "./pages/ApiManagerTool";
import Co2Devices      from "./pages/Co2Devices";
import Profile         from "./pages/Profile";
import Settings        from "./pages/Settings";

function App() {
  const token = localStorage.getItem("token");

  return (
    <Router>
      <Routes>

        {/* ── Root ── */}
        <Route
          path="/"
          element={
            token
              ? <Navigate to="/dashboard" replace />
              : <Navigate to="/login" replace />
          }
        />

        {/* ── Login ── */}
        <Route path="/login" element={<Login />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

        {/* ══════════════════════════════════════════
            DASHBOARD (AdminLayout with sidebar)
        ══════════════════════════════════════════ */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />

          {/* ── Companies ── */}
          <Route path="companies" element={
            <ProtectedRoute allowedRoles={["super_admin", "editor"]}>
              <Companies />
            </ProtectedRoute>
          } />

          <Route path="companies/add" element={
            <ProtectedRoute allowedRoles={["super_admin", "editor"]}>
              <AddCompany />
            </ProtectedRoute>
          } />

          <Route path="companies/edit/:companyId" element={
            <ProtectedRoute allowedRoles={["super_admin", "editor"]}>
              <EditCompany />
            </ProtectedRoute>
          } />

          <Route path="companies/:companyId" element={
            <ProtectedRoute allowedRoles={["super_admin", "editor"]}>
              <CompanyDetails />
            </ProtectedRoute>
          } />

          {/* ── Devices ──
              ⚠️  ORDER MATTERS — specific paths before :uid
          ── */}
          <Route path="devices" element={
            <ProtectedRoute>
              <AllDevices />
            </ProtectedRoute>
          } />

          {/* Add — must be before :uid */}
          <Route path="devices/add" element={
            <ProtectedRoute allowedRoles={["super_admin", "system_manager"]}>
              <AddDevice />
            </ProtectedRoute>
          } />

          {/* Edit — must be before :uid */}
          <Route path="devices/edit/:uid" element={
            <ProtectedRoute allowedRoles={["super_admin", "system_manager"]}>
              <AddDevice />
            </ProtectedRoute>
          } />

          {/* ✅ Device standalone view — uses device_uid as :uid param
              AllDevices navigates to /dashboard/devices/:uid
              DevicePage reads useParams() → { uid } → fetches /api/devices/uid/:uid
          */}
          <Route path="devices/:uid" element={
            <ProtectedRoute>
              <DevicePage />
            </ProtectedRoute>
          } />

          {/* ── Device Health ── */}
          <Route path="device-health" element={
            <ProtectedRoute>
              <DeviceHealth />
            </ProtectedRoute>
          } />

          <Route path="co2-devices" element={
            <ProtectedRoute>
              <Co2Devices />
            </ProtectedRoute>
          } />

          {/* ── Products ── */}
          <Route path="products" element={
            <ProtectedRoute allowedRoles={["super_admin", "editor"]}>
              <Products />
            </ProtectedRoute>
          } />

          <Route path="products/:id" element={
            <ProtectedRoute allowedRoles={["super_admin", "editor"]}>
              <ProductDetails />
            </ProtectedRoute>
          } />

          {/* ── Operations ── */}
          <Route path="alerts"      element={<ProtectedRoute><Alerts /></ProtectedRoute>}      />
          <Route path="maintenance" element={<ProtectedRoute><Maintenance /></ProtectedRoute>} />

          {/* ── Reports ── */}
          <Route path="reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />

          {/* ── ESG ── */}
          <Route path="esg-reports" element={
            <ProtectedRoute allowedRoles={["super_admin", "esg_admin"]}>
              <ESGReports />
            </ProtectedRoute>
          } />

          {/* ── System ── */}
          <Route path="roles" element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <Roles />
            </ProtectedRoute>
          } />

          <Route path="users" element={
            <ProtectedRoute allowedRoles={["super_admin", "editor", "admin", "company_admin"]}>
              <Users />
            </ProtectedRoute>
          } />

          <Route path="admins" element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <Admins />
            </ProtectedRoute>
          } />

          <Route path="profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />

          <Route path="settings" element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } />

          <Route path="api-keys" element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <ApiKeys />
            </ProtectedRoute>
          } />

          <Route path="api-manager/:tab" element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <ApiManagerTool />
            </ProtectedRoute>
          } />

        </Route>

        {/* ══════════════════════════════════════════
            STANDALONE DEVICE (no sidebar)
            Legacy route kept for backward compat.
            New navigation uses /dashboard/devices/:uid above.
        ══════════════════════════════════════════ */}
        <Route path="/device" element={
          <ProtectedRoute>
            <StandaloneLayout />
          </ProtectedRoute>
        }>
          <Route path=":uid" element={<DevicePage />} />
        </Route>

        {/* ── Fallback ── */}
        <Route path="*" element={<Navigate to="/" replace />} />

      </Routes>
    </Router>
  );
}

export default App;
